// =============================================================================
// domain/facets.ts — the global facet filter.
// -----------------------------------------------------------------------------
// PORTED FROM the old client's `tradePassesFacetFilters` — semantics, not code.
//
// The third lens of the global filter, after account and date:
//
//     tradesForActive() = tradesForActiveAccounts()
//                         .filter(tradeInActiveDateRange)
//                         .filter(tradePassesFacetFilters)
//
// ── THE TWO RULES THAT ARE EASY TO INVERT ──
//
// 1. **An EMPTY dimension does not filter.** `instruments: []` means "every
//    instrument", not "no instruments". Getting this backwards empties the whole
//    book the moment the drawer is opened and closed again — and an empty screen
//    reads as lost data, not as a filter.
//
// 2. **AND across dimensions, OR within one.** Selecting ES and NQ shows trades
//    in either; selecting ES and Monday shows ES trades on Mondays. That is the
//    only combination that behaves the way a filter panel looks like it should.
//
// ⚠ Day of week is taken from the EFFECTIVE trade date, so in session mode a
// Monday 23:00 ET trade filters as TUESDAY — its session day. Reading the raw
// date here would silently opt the day-of-week facet out of the day-boundary
// feature, which is the same defect the screen comparison caught twice already.
// =============================================================================

import { dayOfWeek } from './range';
import { effectiveTradeDate, type DatedTrade } from './session';
import type { DayBoundaryMode } from './types';

/** Contract root: `ESU26` → `ES`. Rates, facets and grouping all key on roots. */
export function instrumentRoot(sym: string | null | undefined): string {
  const s = String(sym ?? '').trim().toUpperCase();
  const m = s.match(/^([A-Z0-9]{1,3}?)[FGHJKMNQUVXZ]\d{1,2}$/);
  return m ? (m[1] as string) : s;
}

export interface FacetSelection {
  instruments?: readonly string[];
  daysOfWeek?: readonly number[];
  patterns?: readonly string[];
  /** Custom-tag ids. */
  tags?: readonly string[];
  /** Annotation-completeness values — see `ANNOTATION_OPTS`. */
  annotation?: readonly string[];
}

/** What a facet check needs from a trade. Deliberately not the full Trade. */
export interface FacetTrade extends DatedTrade {
  contract: string | null;
  /** ⚠ Optional: the universe read carries it, older callers may not. */
  patternId?: string | null;
  /** Custom-tag ids. Rides `extras.tags` — a per-user registry, not a column. */
  tags?: readonly string[] | null;
  grade?: string | null;
  notes?: string | null;
  mistakeTags?: readonly string[] | null;
}

// ---------------------------------------------------------------------------
// Annotation completeness
// ---------------------------------------------------------------------------

/**
 * ── 🚨 EVERY PREDICATE IS TRUE WHEN THE TRADE *LACKS* THE PIECE ──
 *
 * This facet finds trades MISSING real review content, so "Missing grade" means
 * "no grade". Reading them the other way inverts the whole dimension while still
 * returning plausible-looking trades.
 *
 * ── 🚨 DELIBERATELY FLAG-AGNOSTIC ──
 *
 * None of these consults `annotated`. The old client's comment says why: that
 * flag is a bare boolean, so a trade bulk-marked annotated can still be empty
 * and would then hide from the Unannotated Queue. **Bypassing the unreliable
 * flag is the entire point of the dimension** — gating on it would reproduce the
 * blindness it exists to fix.
 */
const hasGrade = (t: FacetTrade): boolean => !!(t.grade && String(t.grade).trim());
const hasNotes = (t: FacetTrade): boolean => !!(t.notes && String(t.notes).trim());
const hasMistake = (t: FacetTrade): boolean =>
  Array.isArray(t.mistakeTags) && t.mistakeTags.length > 0;
const hasPattern = (t: FacetTrade): boolean => !!t.patternId;

export interface AnnotationOption {
  value: string;
  label: string;
  pred: (t: FacetTrade) => boolean;
}

export const ANNOTATION_OPTS: readonly AnnotationOption[] = [
  // ⚠ `incomplete` is the AND of three absences — a trade with none of grade,
  // notes or a mistake tag. Not "any of", which is what the four rows below
  // already give you separately.
  {
    value: 'incomplete',
    label: 'Incomplete',
    pred: (t) => !hasGrade(t) && !hasNotes(t) && !hasMistake(t),
  },
  { value: 'no-grade', label: 'Missing grade', pred: (t) => !hasGrade(t) },
  { value: 'no-notes', label: 'Missing notes', pred: (t) => !hasNotes(t) },
  { value: 'no-mistake', label: 'Missing mistake tag', pred: (t) => !hasMistake(t) },
  { value: 'no-pattern', label: 'Missing pattern', pred: (t) => !hasPattern(t) },
];

const ANNOTATION_PRED: Record<string, (t: FacetTrade) => boolean> =
  Object.fromEntries(ANNOTATION_OPTS.map((o) => [o.value, o.pred]));

export function isAnnotationValue(v: string): boolean {
  return Object.prototype.hasOwnProperty.call(ANNOTATION_PRED, v);
}

/** The tag ids on a trade, ignoring ids no longer in the registry. */
export function tradeTagIds(t: FacetTrade, known: ReadonlySet<string>): string[] {
  return (Array.isArray(t.tags) ? t.tags : []).filter((id) => known.has(String(id)));
}

// ---------------------------------------------------------------------------
// The custom-tag registry
// ---------------------------------------------------------------------------

export interface UserTag { id: string; name: string; color: string }

/**
 * `preferences.customTags` → a typed registry.
 *
 * ── ⚠ ONE PARSER, BECAUSE TWO WOULD DISAGREE ABOUT WHICH TAGS EXIST ──
 *
 * The trade form renders a trade's applied tags from this, and the filter drawer
 * builds its option list from it. Two parsers that differed on one edge case —
 * an entry with no `name`, a numeric id — would produce a tag you can apply on
 * one screen and cannot filter by on another, with nothing on either screen
 * looking wrong. It lives in `domain/` because it is a pure shape decision, not
 * a storage one: the jsonb arrives already parsed.
 *
 * ⚠ AN ID WITH NO NAME STILL RENDERS, labelled with the id. A tag applied to a
 * trade and shown as nothing is indistinguishable from no tag at all — and the
 * user would then "add" it again, ending up with two.
 */
export function parseTagRegistry(stored: unknown): readonly UserTag[] {
  if (!Array.isArray(stored)) return [];
  const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
  return (stored as unknown[]).flatMap((x) => {
    if (!x || typeof x !== 'object') return [];
    const r = x as Record<string, unknown>;
    const id = str(r['id']);
    return id ? [{ id, name: str(r['name']) || id, color: str(r['color']) }] : [];
  });
}

/** 0 = Sunday … 6 = Saturday, of the EFFECTIVE date. `null` when undated. */
export function tradeDayOfWeek(
  t: FacetTrade,
  mode: DayBoundaryMode,
): number | null {
  const d = effectiveTradeDate(t, mode);
  if (!d) return null;
  const dow = dayOfWeek(d);
  return Number.isInteger(dow) ? dow : null;
}

export function tradePassesFacets(
  t: FacetTrade,
  f: FacetSelection,
  mode: DayBoundaryMode,
): boolean {
  // ⚠ `?.length` guards BOTH undefined and empty — and empty must not filter.
  if (f.instruments?.length && !f.instruments.includes(instrumentRoot(t.contract))) return false;

  if (f.daysOfWeek?.length) {
    const dow = tradeDayOfWeek(t, mode);
    // ⚠ An undated trade is EXCLUDED when a day filter is active, rather than
    // passed through. "Show me Mondays" cannot honestly include a trade with no
    // day — and passing it would make the count disagree with the list.
    if (dow == null || !f.daysOfWeek.includes(dow)) return false;
  }

  // ⚠ An UNTAGGED trade never matches a pattern filter. The old client compares
  // against `t.tagging?.patternId || ''` and `''` is not in any selection, so
  // "show me VA REVERSION" excludes everything untagged rather than leaking it
  // through — which is what a pattern filter has to mean.
  if (f.patterns?.length) {
    const pid = t.patternId ? String(t.patternId) : '';
    if (!pid || !f.patterns.includes(pid)) return false;
  }

  // ⚠ OR within the dimension, like every other one: a trade needs ANY selected
  // tag, not all of them. An untagged trade matches nothing, for the same reason
  // an unpatterned trade does not leak through a pattern filter.
  if (f.tags?.length) {
    const ids = Array.isArray(t.tags) ? t.tags.map(String) : [];
    if (!f.tags.some((id) => ids.includes(id))) return false;
  }

  // ⚠ OR within the dimension too, and worth stating because it reads
  // backwards: "Missing grade" + "Missing notes" selects trades missing grade OR
  // notes, not both. Each option is already an absence, so ANDing them would
  // narrow to the trades missing everything — which is what `incomplete` is for.
  if (f.annotation?.length) {
    if (!f.annotation.some((v) => ANNOTATION_PRED[v]?.(t))) return false;
  }

  return true;
}

export function applyFacets<T extends FacetTrade>(
  trades: readonly T[],
  f: FacetSelection,
  mode: DayBoundaryMode,
): readonly T[] {
  // Fast path AND a correctness statement: no facets means the input set,
  // untouched and referentially identical, so a memo downstream does not
  // invalidate on every render.
  //
  // ⚠ EVERY dimension must be listed here. A dimension added to
  // `tradePassesFacets` and forgotten in this guard is silently inert — the fast
  // path returns the unfiltered set and the filter looks like it does nothing.
  if (
    !f.instruments?.length && !f.daysOfWeek?.length && !f.patterns?.length
    && !f.tags?.length && !f.annotation?.length
  ) return trades;
  return trades.filter((t) => tradePassesFacets(t, f, mode));
}

/**
 * Instrument options with counts, for the drawer.
 *
 * ⚠ The caller supplies the UNIVERSE, and in the old client that is the
 * account-scoped set over ALL TIME — deliberately NOT date-filtered — "so
 * options stay stable as you narrow (they don't vanish when the view is empty)".
 * Counting from the visible range instead makes options appear and disappear as
 * the date filter moves, which is the behaviour that change was made to fix.
 */
export function instrumentOptions<T extends FacetTrade>(
  universe: readonly T[],
): { value: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const t of universe) {
    const r = instrumentRoot(t.contract);
    if (r) counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([value, count]) => ({ value, label: value, count }));
}

/**
 * Pattern options with counts.
 *
 * ⚠ DRIVEN BY THE CATALOGUE, not by the universe — the opposite of
 * `instrumentOptions`. The old client maps over `state.patterns`, so a pattern
 * with **zero trades still appears, at count 0**. That is deliberate: the list is
 * the user's own named setups, and one vanishing because it has not been traded
 * yet reads as a pattern that was deleted.
 *
 * @param namedPatterns the catalogue, in the order it should be shown. The old
 *                      client does not re-sort it, so neither does this.
 */
export function patternOptions<T extends FacetTrade>(
  universe: readonly T[],
  namedPatterns: readonly { id: string; name: string }[],
): { value: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const t of universe) {
    const pid = t.patternId ? String(t.patternId) : '';
    if (pid) counts.set(pid, (counts.get(pid) ?? 0) + 1);
  }
  return namedPatterns.map((p) => ({
    value: String(p.id),
    label: p.name,
    count: counts.get(String(p.id)) ?? 0,
  }));
}

/**
 * Drop selected pattern ids the catalogue no longer resolves.
 *
 * ⚠ The old client prunes on READ, with its own comment saying why: *"stale
 * tag/pattern ids (deleted from their registry) are pruned on read so a dangling
 * id can't silently filter every trade out."* Without this, deleting a pattern
 * that is currently selected empties the entire book with nothing on screen to
 * explain it — the filter chip would name an id that no longer exists.
 */
export function pruneUnknownPatterns(
  selected: readonly string[],
  namedPatterns: readonly { id: string }[],
): readonly string[] {
  if (!selected.length) return selected;
  const known = new Set(namedPatterns.map((p) => String(p.id)));
  const kept = selected.filter((id) => known.has(String(id)));
  // Referentially identical when nothing was pruned, so a memo does not churn.
  return kept.length === selected.length ? selected : kept;
}

export function dayOfWeekOptions<T extends FacetTrade>(
  universe: readonly T[],
  mode: DayBoundaryMode,
): { value: number; label: string; count: number }[] {
  const LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const counts = new Map<number, number>();
  for (const t of universe) {
    const d = tradeDayOfWeek(t, mode);
    if (d != null) counts.set(d, (counts.get(d) ?? 0) + 1);
  }
  // ⚠ Mon–Fri ALWAYS listed even at zero; weekends only when they carry data.
  // A futures trader expects five weekday rows, and a missing Wednesday reads as
  // a broken filter rather than a quiet week.
  const out: { value: number; label: string; count: number }[] = [];
  for (let d = 1; d <= 5; d++) out.push({ value: d, label: LABELS[d] as string, count: counts.get(d) ?? 0 });
  for (const d of [6, 0]) {
    if (counts.get(d)) out.push({ value: d, label: LABELS[d] as string, count: counts.get(d) as number });
  }
  return out;
}

/**
 * Tag options with counts.
 *
 * ⚠ DRIVEN BY THE REGISTRY, like patterns and unlike instruments — a tag the
 * user created but has not applied yet still appears, at count 0. One vanishing
 * because it is unused reads as a tag that was deleted.
 *
 * @param registry the user's custom tags, in the order they should be shown.
 *                 The old client does not re-sort it, so neither does this.
 */
export function tagOptions<T extends FacetTrade>(
  universe: readonly T[],
  registry: readonly { id: string; name: string; color?: string | null }[],
): { value: string; label: string; count: number; color: string | null }[] {
  const known = new Set(registry.map((x) => String(x.id)));
  const counts = new Map<string, number>();
  for (const t of universe) {
    for (const id of tradeTagIds(t, known)) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return registry.map((tag) => ({
    value: String(tag.id),
    label: tag.name,
    count: counts.get(String(tag.id)) ?? 0,
    color: tag.color ?? null,
  }));
}

/**
 * Annotation options with counts.
 *
 * ⚠ ALL FIVE ARE ALWAYS LISTED, including at zero. "Missing grade: 0" is the
 * answer the user came for — a row that disappears when the work is done looks
 * like a filter that broke rather than a queue that emptied.
 */
export function annotationOptions<T extends FacetTrade>(
  universe: readonly T[],
): { value: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const t of universe) {
    for (const o of ANNOTATION_OPTS) if (o.pred(t)) counts.set(o.value, (counts.get(o.value) ?? 0) + 1);
  }
  return ANNOTATION_OPTS.map((o) => ({
    value: o.value, label: o.label, count: counts.get(o.value) ?? 0,
  }));
}

/**
 * Drop selected tag ids that are no longer in the registry.
 *
 * ⚠ Same rule as `pruneUnknownPatterns` and for the same reason: storage
 * outlives the registry, and a dangling id would filter every trade out with
 * nothing on screen to explain it.
 */
export function pruneUnknownTags(
  selected: readonly string[],
  registry: readonly { id: string }[],
): readonly string[] {
  if (!selected.length) return selected;
  const known = new Set(registry.map((t) => String(t.id)));
  const kept = selected.filter((id) => known.has(String(id)));
  return kept.length === selected.length ? selected : kept;
}
